import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
    Package,
    ExternalLink,
    Image,
    Mail,
    Copy,
    Check,
    Search,
    ChevronDown,
    ChevronUp,
    Globe,
    CreditCard
} from 'lucide-react';
import { generateTrackingUrl, LANGUAGES, CURRENCIES } from '../../utils/tracking';

interface Product {
    id: string;
    name: string;
    description?: string;
    landing_page_url: string;
    active: boolean;
    targeting_rules?: {
        allowed_countries?: string[];
        allowed_devices?: string[];
        allowed_os?: string[];
        fallback_url?: string;
    };
    landing_pages?: { id: string; name: string; url: string }[];
    creatives?: {
        landing_pages?: { id: string; name: string; url: string }[];
        banners?: { id: string; name: string; url: string; dimensions?: string }[];
        email_templates?: { id: string; name: string; subject: string; preview_url?: string }[];
    };
}

export default function AffiliateCreatives() {
    const { user } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [selectedCurrency, setSelectedCurrency] = useState('USD');

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(
                query(collection(db, 'products'), where('active', '==', true))
            );
            const productsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Product[];
            setProducts(productsList);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleProduct = (productId: string) => {
        const newExpanded = new Set(expandedProducts);
        if (newExpanded.has(productId)) {
            newExpanded.delete(productId);
        } else {
            newExpanded.add(productId);
        }
        setExpandedProducts(newExpanded);
    };

    const copyToClipboard = async (text: string, id: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const buildAffiliateLink = (url: string, productId: string) => {
        return generateTrackingUrl({
            baseUrl: url,
            affiliateId: user?.uid || '',
            language: selectedLanguage,
            currency: selectedCurrency,
            productId,
        });
    };

    const filteredProducts = products.filter(product => {
        if (!searchTerm) return true;
        return product.name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const hasCreatives = (product: Product) => {
        return (
            (product.landing_pages?.length || 0) > 0 ||
            (product.creatives?.landing_pages?.length || 0) > 0 ||
            (product.creatives?.banners?.length || 0) > 0 ||
            (product.creatives?.email_templates?.length || 0) > 0
        );
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Marketing Creatives</h1>
                    <p className="text-gray-400">Access landing pages, banners, and email templates</p>
                </div>
            </div>

            {/* Search */}
            <div className="card">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-field pl-12"
                    />
                </div>
            </div>

            {/* Products with Creatives */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : filteredProducts.length === 0 ? (
                <div className="card text-center py-12">
                    <Package className="mx-auto text-gray-500 mb-4" size={48} />
                    <h3 className="text-lg font-semibold text-white mb-2">No Products Found</h3>
                    <p className="text-gray-400">No active products available.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredProducts.map((product) => (
                        <div key={product.id} className="card">
                            {/* Product Header */}
                            <div
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => toggleProduct(product.id)}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-linear-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                                        <Package className="text-white" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">{product.name}</h3>
                                        <div className="flex items-center gap-4 text-sm text-gray-400">
                                            <span className="flex items-center gap-1">
                                                <ExternalLink size={14} />
                                                {(product.landing_pages?.length || 0) + (product.creatives?.landing_pages?.length || 0)} Entry Links
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Image size={14} />
                                                {product.creatives?.banners?.length || 0} Banners
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Mail size={14} />
                                                {product.creatives?.email_templates?.length || 0} Templates
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button className="p-2 text-gray-400 hover:text-white">
                                    {expandedProducts.has(product.id) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                            </div>

                            {/* Expanded Content */}
                            {expandedProducts.has(product.id) && (
                                <div className="mt-6 pt-6 border-t border-white/10 space-y-6">
                                    {/* Language & Currency */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                <Globe size={16} className="inline mr-2" />
                                                Language
                                            </label>
                                            <select
                                                value={selectedLanguage}
                                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                                className="input-field"
                                            >
                                                {LANGUAGES.map(lang => (
                                                    <option key={lang.code} value={lang.code}>
                                                        {lang.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                <CreditCard size={16} className="inline mr-2" />
                                                Currency
                                            </label>
                                            <select
                                                value={selectedCurrency}
                                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                                className="input-field"
                                            >
                                                {CURRENCIES.map(curr => (
                                                    <option key={curr.code} value={curr.code}>
                                                        {curr.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Default Affiliate Link */}
                                    <div className="p-4 bg-primary-500/10 border border-primary-500/20 rounded-lg">
                                        <h4 className="font-medium text-white mb-2">Your Affiliate Link</h4>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                readOnly
                                                value={buildAffiliateLink(product.landing_page_url, product.id)}
                                                className="input-field flex-1 text-sm"
                                            />
                                            <button
                                                onClick={() => copyToClipboard(buildAffiliateLink(product.landing_page_url, product.id), `main-${product.id}`)}
                                                className="btn-primary px-4 flex items-center gap-2"
                                            >
                                                {copiedId === `main-${product.id}` ? <Check size={18} /> : <Copy size={18} />}
                                                {copiedId === `main-${product.id}` ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Entry Links */}
                                    {((product.landing_pages?.length || 0) > 0 || (product.creatives?.landing_pages?.length || 0) > 0) && (
                                        <div>
                                            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                                                <ExternalLink size={18} className="text-primary-400" />
                                                Entry Links
                                            </h4>
                                            <div className="space-y-2">
                                                {[...(product.landing_pages || []), ...(product.creatives?.landing_pages || [])].map((lp) => (
                                                    <div key={lp.id} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
                                                        <div className="flex-1">
                                                            <p className="text-white font-medium">{lp.name}</p>
                                                            <p className="text-xs text-gray-500 truncate">{buildAffiliateLink(lp.url, product.id)}</p>
                                                        </div>
                                                        <a
                                                            href={lp.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 text-gray-400 hover:text-white"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </a>
                                                        <button
                                                            onClick={() => copyToClipboard(buildAffiliateLink(lp.url, product.id), lp.id)}
                                                            className="p-2 text-gray-400 hover:text-white"
                                                        >
                                                            {copiedId === lp.id ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Banners */}
                                    {product.creatives?.banners && product.creatives.banners.length > 0 && (
                                        <div>
                                            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                                                <Image size={18} className="text-emerald-400" />
                                                Banners
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {product.creatives.banners.map((banner) => (
                                                    <div key={banner.id} className="p-3 bg-white/5 rounded-lg">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div>
                                                                <p className="text-white font-medium">{banner.name}</p>
                                                                {banner.dimensions && (
                                                                    <p className="text-xs text-gray-500">{banner.dimensions}</p>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <a
                                                                    href={banner.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-1 text-gray-400 hover:text-white"
                                                                >
                                                                    <ExternalLink size={14} />
                                                                </a>
                                                                <button
                                                                    onClick={() => copyToClipboard(banner.url, banner.id)}
                                                                    className="p-1 text-gray-400 hover:text-white"
                                                                >
                                                                    {copiedId === banner.id ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {banner.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                                            <img
                                                                src={banner.url}
                                                                alt={banner.name}
                                                                className="w-full rounded border border-white/10 mt-2"
                                                                style={{ maxHeight: '100px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Email Templates */}
                                    {product.creatives?.email_templates && product.creatives.email_templates.length > 0 && (
                                        <div>
                                            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                                                <Mail size={18} className="text-blue-400" />
                                                Email Templates
                                            </h4>
                                            <div className="space-y-2">
                                                {product.creatives.email_templates.map((template) => (
                                                    <div key={template.id} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
                                                        <div className="flex-1">
                                                            <p className="text-white font-medium">{template.name}</p>
                                                            <p className="text-xs text-gray-500">Subject: {template.subject}</p>
                                                        </div>
                                                        {template.preview_url && (
                                                            <a
                                                                href={template.preview_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 text-gray-400 hover:text-white"
                                                            >
                                                                <ExternalLink size={16} />
                                                            </a>
                                                        )}
                                                        <button
                                                            onClick={() => copyToClipboard(template.subject, template.id)}
                                                            className="p-2 text-gray-400 hover:text-white"
                                                            title="Copy subject line"
                                                        >
                                                            {copiedId === template.id ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* No Creatives */}
                                    {!hasCreatives(product) && (
                                        <div className="text-center py-8 text-gray-500">
                                            <Package size={32} className="mx-auto mb-2 opacity-50" />
                                            <p>No creatives available for this product yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
